import type { Workflow } from '@/stores/workflowStore';
import type { InlineValidationState } from '@/services/inlineValidation';
import { getMissionGoalIntent } from '@/lib/mapNodeSummary';
import type { SimulatedFlowTrace } from '@/services/flowTraceSimulation';

export interface FlowTraceLike {
  completedNodeIds: string[];
  failedNodeId: string | null;
  running: boolean;
}

export interface FlowTracingTruth {
  state: 'mismatch' | 'partial' | 'match';
  score: number;
  confidence: number;
  flowComplete: boolean;
  label: string;
  description: string;
  reasons: string[];
  recommendations: string[];
}

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((t) => t.length >= 3 && !['the', 'and', 'for', 'with', 'this', 'that', 'from'].includes(t));
}

function collectFlowCorpus(workflow: Workflow): string {
  return workflow.nodes
    .filter((n) => n.type !== 'start' && !(n.type === 'goal' && n.subtype === 'outcome'))
    .map((n) =>
      [
        String(n.subtype ?? ''),
        String(n.config.contextLabel ?? ''),
        String(n.config.contextPurpose ?? ''),
        String(n.config._autoBlocksRationale ?? ''),
        String(n.config.message ?? ''),
        String(n.config.agentBrief ?? ''),
        String(n.config.droneTask ?? ''),
      ]
        .join(' ')
        .trim()
    )
    .join(' ');
}

function vectorize(text: string): Map<string, number> {
  const tokens = tokenize(text);
  const map = new Map<string, number>();
  for (const t of tokens) map.set(t, (map.get(t) ?? 0) + 1);
  return map;
}

function cosine(a: Map<string, number>, b: Map<string, number>): number {
  const keys = new Set([...a.keys(), ...b.keys()]);
  let dot = 0;
  let na = 0;
  let nb = 0;
  for (const k of keys) {
    const av = a.get(k) ?? 0;
    const bv = b.get(k) ?? 0;
    dot += av * bv;
    na += av * av;
    nb += bv * bv;
  }
  if (!na || !nb) return 0;
  return dot / (Math.sqrt(na) * Math.sqrt(nb));
}

export function evaluateFlowTracingTruth(
  workflow: Workflow,
  trace?: FlowTraceLike,
  analysis?: InlineValidationState | null,
  simulation?: SimulatedFlowTrace | null
): FlowTracingTruth {
  const goal = workflow.nodes.find((n) => n.type === 'goal' && n.subtype === 'outcome');
  const goalId = goal?.id ?? null;
  const flowComplete = Boolean(
    trace && !trace.running && !trace.failedNodeId && goalId && trace.completedNodeIds.includes(goalId)
  );

  const intent = getMissionGoalIntent(workflow);
  const corpus = collectFlowCorpus(workflow);
  const score = cosine(vectorize(intent), vectorize(corpus));
  const confidenceBase = Math.min(1, Math.max(0, score));

  const errors = analysis ? analysis.suggestions.filter((s) => s.type === 'error').length : 0;
  const hasOperational = workflow.nodes.some(
    (n) => n.type === 'trigger' || n.type === 'tool' || n.type === 'action' || n.type === 'agent' || n.type === 'drone'
  );
  const strictness = workflow.missionMeta?.truthFactorStrictness ?? 'balanced';
  const thresholds =
    strictness === 'strict'
      ? { mismatch: 0.35, match: 0.7 }
      : strictness === 'lenient'
        ? { mismatch: 0.2, match: 0.45 }
        : { mismatch: 0.25, match: 0.55 };

  let state: FlowTracingTruth['state'];
  if (!hasOperational || score < thresholds.mismatch) state = 'mismatch';
  else if (score < thresholds.match) state = 'partial';
  else state = 'match';
  if (state === 'match' && errors > 0) state = 'partial';

  const reasons: string[] = [];
  const recommendations: string[] = [];
  if (score >= 0.55) reasons.push('Operational block context semantically matches stated intent.');
  else if (score >= 0.3) reasons.push('Partial semantic match between intent and configured block purposes.');
  else reasons.push('Low semantic overlap between intent and configured block purposes.');

  if (errors > 0) {
    reasons.push(`${errors} blocking validation issue(s) reduce truth factor.`);
    recommendations.push('Use Smart Blocks and fix highlighted validation errors.');
  }
  if (simulation && !simulation.predictedComplete) {
    reasons.push('Pre-run simulation predicts the flow does not complete.');
    recommendations.push('Resolve simulated failpoints and required human-input blocks.');
  }
  if (simulation?.blockedNodeIds.length) {
    recommendations.push('Complete Smart Blocks input prompts for orange-pulsing blocks.');
  }
  if (trace?.failedNodeId) {
    reasons.push('Latest execution failed before reaching GOAL.');
    recommendations.push('Inspect the failed block and apply Smart Blocks or manual fixes.');
  }
  if (flowComplete) {
    reasons.push('Last execution reached GOAL successfully.');
  }

  const label =
    state === 'match'
      ? flowComplete
        ? 'Intent match · Flow complete'
        : 'Intent match'
      : state === 'partial'
        ? 'Intent partly matched'
        : 'Intent mismatch';

  const description =
    state === 'match'
      ? flowComplete
        ? 'Current flow behavior matches the stated outcome and has completed.'
        : 'Current flow behavior aligns with the stated outcome.'
      : state === 'partial'
        ? 'Flow is partially aligned; refine blocks/config to fully match the outcome.'
        : 'Flow behavior does not yet support the stated outcome.';

  const confidencePenalty = Math.min(0.45, errors * 0.08 + (simulation && !simulation.predictedComplete ? 0.12 : 0));
  const confidence = Math.max(0, Math.min(1, confidenceBase - confidencePenalty));

  return { state, score, confidence, flowComplete, label, description, reasons, recommendations };
}
